(function () {
  'use strict';

  /* ==========================================
     HERO VIDEO — cinematic reveal & lifecycle
     ========================================== */
  function initHeroVideo() {
    var video = document.getElementById('onyxBgVideo');
    var fallback = document.querySelector('.onyx-hero-fallback-img');
    if (!video) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      video.pause();
      return;
    }

    /* R85 (ONYX-IMPLEMENTATION-SPEC.md Phase 2, item 5 — the one piece of
       the video fix not yet landed): bail out under Data Saver the same
       way as reduced-motion. The poster (.onyx-hero-fallback-img) is
       already a complete, always-visible design on its own — nothing else
       needs to change for this to be a correct, static end state. */
    var saveData = !!(navigator.connection && navigator.connection.saveData);
    if (saveData) {
      video.preload = 'none';
      video.pause();
      return;
    }

    var revealed = false;

    function reveal() {
      if (revealed) return;
      revealed = true;
      requestAnimationFrame(function () {
        /* R48 (ONYX-IMPLEMENTATION-SPEC.md Phase 2): was video.style.opacity = '1'
           (an inline style), while assets/onyx-header.css already has a correct
           .onyx-bg-video.is-revealed rule AND a will-change:auto reset keyed to
           that same class. Setting the inline style directly did reveal the
           video, but it never added the class, so the will-change:opacity /
           backface-visibility:hidden GPU layer promotion on .onyx-bg-video's
           base rule stayed permanently applied even after the reveal finished —
           confirmed live via getComputedStyle. Using the class instead lets the
           already-written CSS do its job. */
        video.classList.add('is-revealed');
        if (fallback) fallback.classList.add('is-hidden');
      });
    }

    /* R50: owner reported a flicker/jolt right as the video starts. Revealing
       on the very first decoded frame can catch the decoder before it's
       settled into steady playback (a common source of a visible stutter
       right at the cut from the static poster to the video). Waiting for a
       few frames — still well under 100ms once playback has actually begun —
       gives it a moment to stabilise before the crossfade starts. */
    function onActualPlay() {
      if (revealed) return;
      if ('requestVideoFrameCallback' in video) {
        var framesSeen = 0;
        var waitForStableFrame = function() {
          framesSeen++;
          if (framesSeen >= 3) {
            requestAnimationFrame(reveal);
          } else {
            video.requestVideoFrameCallback(waitForStableFrame);
          }
        };
        video.requestVideoFrameCallback(waitForStableFrame);
      } else {
        var checkTime = function() {
          if (video.currentTime > 0.15) {
            video.removeEventListener('timeupdate', checkTime);
            requestAnimationFrame(reveal);
          }
        };
        video.addEventListener('timeupdate', checkTime);
      }
    }

    video.addEventListener('playing', onActualPlay);

    /* R104 safety net. Now that the element carries `autoplay` again (see the
       comment on the <video> tag in sections/header.liquid), playback can begin
       BEFORE this file has run — which is the entire point, but it means the
       'playing' event above may already have fired and been missed. Without
       this, the reveal would never be triggered and the video would sit at
       opacity 0 forever with only the poster visible. attemptPlay() already
       covers the usual route via its `else if (!revealed)` branch, but that
       only runs if IntersectionObserver exists; this makes the recovery
       unconditional and immediate. */
    if (!video.paused && video.readyState >= 2) {
      onActualPlay();
    }

    function attemptPlay(isRecovery) {
      if (document.visibilityState === 'hidden') return;

      if (isRecovery && video.readyState < 2 && (video.networkState === 0 || video.networkState === 3)) {
        video.load();
      }

      if (video.paused) {
        var p = video.play();
        if (p !== undefined) {
          p.catch(function(){}); // Silently handle Low Power Mode
        }
      } else if (!revealed) {
        onActualPlay();
      }

      if (isRecovery) recoverIfStalled();
    }

    /* R53: the real "video is frozen after closing and reopening the browser"
       bug. Every recovery path above is gated on video.paused — but the
       failure mode being reported is a video the browser still reports as
       PLAYING (paused === false) whose decoder has actually stalled, so no
       frames advance. Traced through attemptPlay(): such a video hits the
       `else if (!revealed)` branch, `revealed` is already true by then, and
       nothing happens — it stays frozen forever. The video.load() rescue
       above can't fire either, because it requires readyState < 2 while a
       stalled decoder typically sits at readyState >= 2.
       This detects the stall by fact rather than by flag: sample currentTime,
       wait, and see whether it actually moved. Only reloads when it truly
       didn't. currentTime is restored after the reload so the recovery is
       seamless instead of snapping the loop back to frame 0 — a visible jump
       was itself reported as a jolt on return. */
    var stallCheckTimer = null;
    function recoverIfStalled() {
      if (stallCheckTimer) clearTimeout(stallCheckTimer);
      if (video.paused || document.visibilityState === 'hidden') return;

      var timeBefore = video.currentTime;
      stallCheckTimer = setTimeout(function () {
        stallCheckTimer = null;
        if (video.paused || document.visibilityState === 'hidden') return;
        if (video.currentTime > timeBefore) return; // advancing normally

        var resumeAt = video.currentTime;
        var onMeta = function () {
          video.removeEventListener('loadedmetadata', onMeta);
          try {
            if (resumeAt > 0 && resumeAt < video.duration) video.currentTime = resumeAt;
          } catch (e) {}
          var p = video.play();
          if (p !== undefined) p.catch(function () {});
        };
        video.addEventListener('loadedmetadata', onMeta);
        video.load();
      }, 400);
    }

    function wakeUpVideo() {
      if (!video.paused) return; 
      var p = video.play();
      if (p !== undefined) {
        p.then(function() {
          ['touchstart', 'click', 'scroll'].forEach(function(evt) {
            document.removeEventListener(evt, wakeUpVideo);
          });
        }).catch(function(){});
      }
    }
    
    ['touchstart', 'click', 'scroll'].forEach(function(evt) {
      document.addEventListener(evt, wakeUpVideo, { once: true, passive: true });
    });

    if ('IntersectionObserver' in window) {
      var heroObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              attemptPlay(false);
            } else {
              if (!video.paused) {
                video.pause();
              }
            }
          });
        },
        { threshold: 0, rootMargin: "150px 0px" } 
      );
      heroObserver.observe(video);
    }

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible') {
        attemptPlay(true);
      }
    });

    /* R52: owner reported the video sometimes freezes after fully closing
       and reopening the browser — a real tab close + mobile OS/browser
       session restore, not the same thing as a simple back-navigation.
       That path doesn't reliably set event.persisted (it isn't a true
       bfcache suspend/resume, which is the only case this used to check
       for), so the recovery attempt below never ran for it. Attempting
       recovery on every pageshow is harmless when the video is already
       fine — attemptPlay() is a no-op safety check when playback is
       already healthy — so there is no cost to no longer gating it. */
    window.addEventListener('pageshow', function() {
      attemptPlay(true);
    });

    if (!video.paused && video.readyState > 0) {
      onActualPlay();
    } else {
      attemptPlay(false);
    }
  }

  /* ==========================================
     HEADER UI (SIDEBARS & MENUS)
     ========================================== */
  function initHeaderUI() {
    var header = document.getElementById('onyxHeader');
    var langBtn = document.getElementById('onyxLangBtn');
    var drawerLangBtn = document.getElementById('onyxDrawerLangBtn');
    
    // Menu Drawer
    var menuToggle = document.getElementById('onyxMenuToggle');
    var drawerClose = document.getElementById('onyxDrawerClose');
    var drawerBackdrop = document.getElementById('onyxDrawerBackdrop');
    var drawer = document.getElementById('onyxMobileDrawer');
    var drawerAccountLink = document.getElementById('onyxDrawerAccountLink');

    // Search Sidebar
    var searchToggle = document.getElementById('onyxSearchToggle');
    var searchSidebar = document.getElementById('onyxSearchSidebar');
    var searchClose = document.getElementById('onyxSearchClose');
    var searchBackdrop = document.getElementById('onyxSearchBackdrop');
    var searchInput = document.getElementById('onyxSearchInput');

    // Account Sidebar
    var accountToggle = document.getElementById('onyxAccountToggle');
    var accountSidebar = document.getElementById('onyxAccountSidebar');
    var accountClose = document.getElementById('onyxAccountClose');
    var accountBackdrop = document.getElementById('onyxAccountBackdrop');

    // Cart Sidebar
    var cartToggle = document.getElementById('onyxCartToggle');
    var cartSidebar = document.getElementById('onyxCartSidebar');
    var cartClose = document.getElementById('onyxCartClose');
    var cartBackdrop = document.getElementById('onyxCartBackdrop');

    function closeAllSidebars() {
      if (drawer) drawer.classList.remove('is-active');
      if (drawerBackdrop) drawerBackdrop.classList.remove('is-active');
      
      if (searchSidebar) searchSidebar.classList.remove('is-active');
      if (searchBackdrop) searchBackdrop.classList.remove('is-active');
      
      if (accountSidebar) accountSidebar.classList.remove('is-active');
      if (accountBackdrop) accountBackdrop.classList.remove('is-active');
      
      if (cartSidebar) cartSidebar.classList.remove('is-active');
      if (cartBackdrop) cartBackdrop.classList.remove('is-active');
    }

    // Toggle Functions
    function openMenu() { closeAllSidebars(); drawer.classList.add('is-active'); drawerBackdrop.classList.add('is-active'); }
    function openSearch() { 
      closeAllSidebars(); 
      searchSidebar.classList.add('is-active'); 
      searchBackdrop.classList.add('is-active'); 
      setTimeout(function () { if (searchInput) searchInput.focus(); }, 400); 
    }
    function openAccount() { closeAllSidebars(); accountSidebar.classList.add('is-active'); accountBackdrop.classList.add('is-active'); }
    function openCart() { closeAllSidebars(); cartSidebar.classList.add('is-active'); cartBackdrop.classList.add('is-active'); }
    window.onyxOpenCartSidebar = openCart;

    // Cart sidebar rendering — shared across every page (product page add-to-cart,
    // quantity stepper), since this file is loaded globally via the header section.
    function withWidth(url, width) {
      if (!url) return url;
      return url.indexOf('?') > -1 ? url + '&width=' + width : url + '?width=' + width;
    }
    function formatMoney(cents, currency) {
      return (Number(cents) / 100).toFixed(2) + ' ' + currency;
    }
    function onyxRootUrl() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }
    function updateCartBadge(count) {
      var badge = document.getElementById('onyxCartCountBadge');
      if (!badge) return;
      badge.textContent = count;
      badge.classList.toggle('is-hidden', count === 0);
    }

    function renderCartItemsHtml(cart) {
      var isArabic = document.documentElement.lang === 'ar';
      return cart.items.map(function (item) {
        return '' +
          '<div class="onyx-cart-item" data-key="' + item.key + '">' +
            '<img src="' + withWidth(item.image, 160) + '" alt="' + item.product_title + '" class="onyx-cart-item-img">' +
            '<div class="onyx-cart-item-details">' +
              '<a href="' + item.url + '" class="onyx-cart-item-title">' + item.product_title + '</a>' +
              '<span class="onyx-cart-item-price">' + formatMoney(item.final_line_price, cart.currency) + '</span>' +
              '<div class="onyx-cart-item-qty-stepper" role="group" aria-label="' + (isArabic ? 'الكمية' : 'Quantity') + '">' +
                '<button type="button" class="onyx-qty-btn" data-qty-change="-1" aria-label="' + (isArabic ? 'تقليل' : 'Decrease') + '">−</button>' +
                '<span class="onyx-qty-value">' + item.quantity + '</span>' +
                '<button type="button" class="onyx-qty-btn" data-qty-change="1" aria-label="' + (isArabic ? 'زيادة' : 'Increase') + '">+</button>' +
              '</div>' +
            '</div>' +
          '</div>';
      }).join('');
    }

    function renderCartSidebarFromCart(cart, errorMessage) {
      var sidebarBody = document.querySelector('#onyxCartSidebar .onyx-search-sidebar-body');
      if (!sidebarBody) return;
      var isArabic = document.documentElement.lang === 'ar';
      var rootUrl = onyxRootUrl();
      var errorHtml = errorMessage ? '<div class="onyx-cart-error">' + errorMessage + '</div>' : '';

      if (cart.item_count === 0) {
        /* R64 (Phase 3e): spec flagged the empty cart state as "prime space"
           for the COD/shipping/warranty promises — reused the exact same
           icons/copy and the .onyx-drawer-trust/-section-label classes
           already built for the mobile drawer (Phase 3d) rather than
           inventing a second version of this component. */
        var emptyCartTrustHtml =
          '<div class="onyx-drawer-section" style="margin-top: 40px; text-align: start;">' +
            '<span class="onyx-drawer-section-label">' + (isArabic ? 'ليه أونيكس' : 'WHY ONYX') + '</span>' +
            '<div class="onyx-drawer-trust">' +
              '<span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>' + (isArabic ? 'الدفع عند الاستلام' : 'Cash on Delivery') + '</span>' +
              '<span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.5"/><circle cx="18" cy="18" r="1.5"/></svg>' + (isArabic ? 'شحن مجاني 2-4 أيام' : 'Free Shipping 2-4 Days') + '</span>' +
              '<span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z"/></svg>' + (isArabic ? 'ضمان سنة' : '1-Year Warranty') + '</span>' +
            '</div>' +
          '</div>';
        sidebarBody.innerHTML = errorHtml +
          '<div class="onyx-stagger-item" style="text-align: center; padding-top: 40px; color: #a1a1a6;">' +
            '<svg width="45" height="45" viewBox="0 0 24 24" fill="none" stroke="var(--onyx-gold)" stroke-width="1.5" style="margin-bottom: 20px;"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
            '<p style="font-size: 1.1rem;">' + (isArabic ? 'سلة مشترياتك فارغة حالياً.' : 'Your cart is currently empty.') + '</p>' +
            '<a href="' + rootUrl + 'collections/all" class="onyx-tag" style="margin-top: 20px;">' + (isArabic ? 'تسوق الآن' : 'Continue Shopping') + '</a>' +
          '</div>' +
          emptyCartTrustHtml;
      } else {
        sidebarBody.innerHTML = errorHtml +
          '<div class="onyx-cart-items-wrapper onyx-stagger-item">' + renderCartItemsHtml(cart) + '</div>' +
          '<div class="onyx-cart-footer onyx-stagger-item">' +
            '<div class="onyx-cart-subtotal">' +
              '<span>' + (isArabic ? 'الإجمالي:' : 'Subtotal:') + '</span>' +
              '<span>' + formatMoney(cart.total_price, cart.currency) + '</span>' +
            '</div>' +
            '<div style="display: flex; flex-direction: column; gap: 12px;">' +
              '<form action="' + rootUrl + 'cart" method="post" id="cart" class="onyx-cart-form" style="margin: 0;">' +
                '<button type="submit" name="checkout" class="btn-primary onyx-full-btn">' + (isArabic ? 'إتمام الطلب' : 'Checkout') + '</button>' +
              '</form>' +
              '<a href="' + rootUrl + 'cart" class="btn-secondary onyx-full-btn">' + (isArabic ? 'عرض تفاصيل السلة' : 'View Cart') + '</a>' +
            '</div>' +
          '</div>';
      }
      updateCartBadge(cart.item_count);
    }

    function fetchCartAndRender(errorMessage) {
      return fetch(onyxRootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (res) { return res.json(); })
        .then(function (cart) { renderCartSidebarFromCart(cart, errorMessage); return cart; })
        .catch(function () {});
    }
    window.onyxRenderCartSidebar = fetchCartAndRender;

    // Quantity stepper — delegated so it works on any page where the sidebar renders.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.onyx-qty-btn');
      if (!btn) return;
      var item = btn.closest('.onyx-cart-item');
      var valueEl = item && item.querySelector('.onyx-qty-value');
      if (!item || !valueEl) return;
      var key = item.getAttribute('data-key');
      if (!key) return;

      var itemButtons = item.querySelectorAll('.onyx-qty-btn');
      if (itemButtons[0].disabled) return;
      itemButtons.forEach(function (b) { b.disabled = true; });

      var next = Math.max(0, (parseInt(valueEl.textContent, 10) || 0) + parseInt(btn.getAttribute('data-qty-change'), 10));
      var isArabic = document.documentElement.lang === 'ar';
      var errorText = isArabic ? 'تعذّر تحديث الكمية. حاول مرة أخرى.' : 'Unable to update quantity. Please try again.';

      if (next === 0) {
        item.remove();
      } else {
        valueEl.textContent = next;
      }

      fetch(onyxRootUrl() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: next })
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok) {
            console.error('[onyx-cart] change failed:', result.data && (result.data.description || result.data.message));
            return fetchCartAndRender(errorText);
          }
          renderCartSidebarFromCart(result.data);
        })
        .catch(function () { fetchCartAndRender(errorText); });
    });

    // 🔥 الحل الجذري لمشكلة زرار الحساب في الموبايل 🔥
    if (drawerAccountLink) {
      drawerAccountLink.addEventListener('click', function(e) {
        e.preventDefault(); 
        e.stopPropagation(); // السر هنا: بيمنع المستعرض من قفل القائمة الجديدة بالغلط

        if (drawer) drawer.classList.remove('is-active');
        if (drawerBackdrop) drawerBackdrop.classList.remove('is-active');
        
        if (accountSidebar) accountSidebar.classList.add('is-active');
        if (accountBackdrop) accountBackdrop.classList.add('is-active');
      });
    }

    // 🔥 حل مشكلة نزول السيكشن (About Onyx) وقفل القائمة أوتوماتيكياً 🔥
    var drawerNavLinks = document.querySelectorAll('.onyx-drawer-link');
    drawerNavLinks.forEach(function(link) {
      link.addEventListener('click', function() {
        // يقفل القائمة لأي رابط ما عدا زرار الحساب عشان ميتعارضش
        if (this.id !== 'onyxDrawerAccountLink') {
          closeAllSidebars();
        }
      });
    });

    if (menuToggle) menuToggle.addEventListener('click', openMenu);
    if (drawerClose) drawerClose.addEventListener('click', closeAllSidebars);
    if (drawerBackdrop) drawerBackdrop.addEventListener('click', closeAllSidebars);

    if (searchToggle) { searchToggle.addEventListener('click', function (e) { e.preventDefault(); openSearch(); }); }
    if (searchClose) searchClose.addEventListener('click', closeAllSidebars);
    if (searchBackdrop) searchBackdrop.addEventListener('click', closeAllSidebars);

    if (accountToggle) { accountToggle.addEventListener('click', function (e) { e.preventDefault(); openAccount(); }); }
    if (accountClose) accountClose.addEventListener('click', closeAllSidebars);
    if (accountBackdrop) accountBackdrop.addEventListener('click', closeAllSidebars);

    if (cartToggle) { cartToggle.addEventListener('click', function (e) { e.preventDefault(); openCart(); }); }
    if (cartClose) cartClose.addEventListener('click', closeAllSidebars);
    if (cartBackdrop) cartBackdrop.addEventListener('click', closeAllSidebars);

    // Close when clicking outside
    document.addEventListener('click', function (e) {
      if (drawer && drawer.classList.contains('is-active') && !drawer.contains(e.target) && menuToggle && !menuToggle.contains(e.target)) closeAllSidebars();
      if (searchSidebar && searchSidebar.classList.contains('is-active') && !searchSidebar.contains(e.target) && searchToggle && !searchToggle.contains(e.target)) closeAllSidebars();
      if (accountSidebar && accountSidebar.classList.contains('is-active') && !accountSidebar.contains(e.target) && accountToggle && !accountToggle.contains(e.target)) closeAllSidebars();
      if (cartSidebar && cartSidebar.classList.contains('is-active') && !cartSidebar.contains(e.target) && cartToggle && !cartToggle.contains(e.target)) closeAllSidebars();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllSidebars();
    });

    /* R21d — the language switcher's JS is gone on purpose.
       Both language buttons are now real submit buttons inside Shopify's own
       {% form 'localization' %} (sections/header.liquid), which emits
       form_type, utf8, _method and return_to itself and needs no JavaScript
       at all. Re-adding a click handler here would submit a second form on
       top of the native one — if a locale switch ever needs changing, change
       the markup, not this file. langBtn / drawerLangBtn are still looked up
       above only because other handlers below reference the header layout. */

    // Header Scroll State
    if (header) {
      var lastScrollY = window.scrollY;
      var ticking = false;

      /* R102: the header visibly jumped on every reload made below the fold
         ("بيحصل رعشة للعناصر مع الريستارت في الهيدر"). Two independent causes,
         both handled here — see also .onyx-header--boot in onyx-header.css.

         1. Restoring the scroll position on reload is reported to the page as
            an ordinary scroll event. The old code compared it against
            lastScrollY (still 0 at that point) and read the restore as "the
            user is scrolling down", so it slid the entire header out of view
            with .is-hidden and then slid it back on the next event — a real
            movement, not a repaint. Direction is only trusted from the point
            the page has finished loading, so a restored position now settles
            the header's state without ever animating it away. Nothing changes
            for genuine scrolling: hide-on-down/show-on-up behaves as before.

         2. Whichever state the header lands in at boot was reached THROUGH the
            0.4-0.5s transition on height/background/box-shadow. The boot class
            below suppresses transitions for that first applied state only. */
      var trustDirection = false;

      header.classList.add('onyx-header--boot');

      function updateHeaderState() {
        var currentScrollY = window.scrollY;

        if (currentScrollY > 50) {
          if (trustDirection && currentScrollY > lastScrollY) {
            // وأنت نازل: الهيدر يختفي فوراً بدون أي بلور
            header.classList.add('is-hidden');
            header.classList.remove('is-scrolled');
          } else {
            // وأنت طالع: الهيدر يظهر بالبلور عشان يكون واضح فوق محتوى الموقع
            header.classList.remove('is-hidden');
            header.classList.add('is-scrolled');
          }
        } else {
          // لما توصل لأعلى الصفحة: يرجع شفاف تماماً
          header.classList.remove('is-scrolled');
          header.classList.remove('is-hidden');
        }

        lastScrollY = currentScrollY;
        ticking = false;
      }

      window.addEventListener(
        'scroll',
        function () {
          if (!ticking) {
            window.requestAnimationFrame(updateHeaderState);
            ticking = true;
          }
        },
        { passive: true }
      );

      updateHeaderState();

      /* Two frames, not one: the first lets the boot state be committed, the
         second guarantees it has actually been painted before transitions are
         allowed to run again. lastScrollY is re-read at the same moment so the
         first genuine scroll is measured from where the page really is, not
         from where it was before the browser restored the position. */
      function releaseHeaderBoot() {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            header.classList.remove('onyx-header--boot');
            lastScrollY = window.scrollY;
            trustDirection = true;
          });
        });
      }

      if (document.readyState === 'complete') {
        releaseHeaderBoot();
      } else {
        window.addEventListener('load', releaseHeaderBoot, { once: true });
      }
    }
  }

  function boot() {
    initHeroVideo();
    initHeaderUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();